/* eslint-disable func-names */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-this-in-sfc */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react-lite';
import {
  CalculateButtonContainer,
  DataInputField,
  DataInputFields,
  DataInputFieldsContainer,
  DataInputLabelButton,
  DimensionFlexContainer,
  DividerPipe,
  MessagesContainer,
  CalculatoContainer,
  DesktopWidthLimitCalculator,
} from './widget-calculator.styles';
import { store, useStore } from '../../../stores/store';
import {
  getLengthConfig,
  getWeightConfig,
} from '../../parcel-creation/parcel-details-dimensions/dimensionsConfig';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { getShipmentCountriesByFilter } from '../../../utils/parcel-creation/parcel-creation-utils';
import InputCountryFormField from './components/input-country-form-field/input-country-form-field.component';
import InputZipFormField from './components/input-country-form-field/input-zip-form-field.component';
import EstimatedDeliveryCost from './components/estimated-delivery-cost/estimated-delivery-cost.component';
import Button from '../../../common/button/button.component';
import ValidationHandler from './components/validation-handler/validation-handler.component';
import MeasureInput from './components/measure-input/measure-input.component';
import { getMaxValidation, getMinValidation } from './helpers';
import { useInputsRef } from './use-inputs-ref';
import FormFieldText from '../../../common/form-field-text/form-field-text.component';
import VolumetricWeightMessage from './components/volumetric-weight-message/volumetric-weight-message.component';
import { client } from '../../../config';
import { theme } from '../../../styles/themes';
import { useMediaQuery } from '../../../hooks/use-media-query.hook';
import { prepareEcommerceItemForCalculator } from '../../../services/analytics/helpers';
import {
  DepartureFieldEffectsCommon,
  DepartureFieldEffectsCountry,
  DepartureFieldEffectsZip,
  DestinationFieldEffectSearchParam,
  DestinationFieldEffectsCountry,
  DestinationFieldEffectsZip,
  DimensionFieldEffects,
} from './widget-calculator-effects';
import {
  AutocompleteOptions,
  GeoapifyAutocompleteType,
  GeoapifyService,
  geopifyGetPredictions,
} from '../../../utils/geoapify/geoapify';
import { ApiResponseStatus } from '../../../constants/server';
import { CountryDetails } from '../../../models/shipmentCountryInfo';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import { isMeest } from '../../../utils/client/client.utils';

const isMeestClient = isMeest(client);

function numberTransform(value: any, originalValue: any) {
  if (
    originalValue === undefined ||
    originalValue === null ||
    originalValue === ''
  ) {
    return undefined;
  }

  return Number.isNaN(value) ? undefined : value;
}

function isShipmentCountry(x: unknown): x is CountryDetails {
  return (
    !!x &&
    typeof x === 'object' &&
    'code' in (x as any) &&
    'zipCode' in (x as any)
  );
}

export type IDataInputNames =
  | 'departure_search_string'
  | 'destination_search_string'
  | 'departure_country'
  | 'destination_country'
  | 'weight'
  | 'size_z'
  | 'size_x'
  | 'size_y';

interface ValidationContext {
  departureNotSupported?: boolean;
  destinationNotSupported?: boolean;
  predictionsNotFoundDeparture?: boolean;
  predictionsNotFoundDestination?: boolean;
  departureSelectionConfirmed?: boolean;
  destinationSelectionConfirmed?: boolean;
}

export interface FormValues {
  departure_search_string: string;
  destination_search_string: string;
  departure_country: CountryDetails | null;
  destination_country: CountryDetails | null;
  weight: number | undefined;
  size_z: number | undefined;
  size_x: number | undefined;
  size_y: number | undefined;
  volumetricWeight: number;

  validationContext: ValidationContext | null;
}

interface Props {
  isFrameMode?: boolean;
  hidden?: boolean;
  isDomesticFlow?: boolean;
  useZipSearchForDeparture?: boolean;
  useZipSearchForDestination?: boolean;
}

const WidgetCalculatorForm = ({
  isFrameMode,
  hidden,
  isDomesticFlow,
  useZipSearchForDeparture,
  useZipSearchForDestination,
}: Props) => {
  const {
    userStore: { measures },
    localizationsStore: { selectedCountry, selectedLocalization },
    shipmentCountriesStore: {
      senderCountries,
      receiverCountries,
      isDirectionsListApiLoading,
      receiverPinnedList,
    },
    widgetCalculatorStore: {
      deliveryLimitations,
      isCalculating,
      isLoadingLimitations,
      makeShipmentCalculation,
      setIsCalculating,
      setDepartureCountry,
      setDestinationCountry,
      showEstimatedDeliveryCost,
      setShowEstimatedDeliveryCost,
      resetStore,
      findShipmentCountry,
      routeNotFoundError,
    },
    commonStore: { analyticsManager },
  } = useStore();

  const { openModal } = useModal();

  const DEFAULT_INPUTS_TO_SHOW = {
    departure_search_string: !useZipSearchForDeparture,
    destination_search_string: false,
    weight: false,
    size_z: false,
    size_x: false,
    size_y: false,
  };

  const [inputsToShow, setInputsToShow] = useState(DEFAULT_INPUTS_TO_SHOW);
  const [activeField, setActiveField] = useState<IDataInputNames | null>(null);
  const formikRef = useRef<FormikProps<FormValues> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery(`(max-width: ${DesktopWidthLimitCalculator})`);

  const { inputWeightRef, inputLengthRef, inputWidthRef, inputHeightRef } =
    useInputsRef({ inputsToShow });

  const [showClearButton, setShowClearButton] = useState(false);

  const [isDestinationLocked, setIsDestinationLocked] = useState(false);

  useEffect(() => {
    if (!routeNotFoundError) return;

    openModal({
      id: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
      name: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
      props: {
        title: routeNotFoundError?.title,
        body: routeNotFoundError?.message,
        showNotifyButton: false,
      },
    });
  }, [routeNotFoundError, openModal]);

  useEffect(() => {
    resetStore();

    return () => resetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowClearButton(false);
    setShowEstimatedDeliveryCost(false);
  }, [selectedLocalization, setShowEstimatedDeliveryCost]);

  // Required for dynamic resizing of parent component in iframe mode
  useEffect(() => {
    if (!isFrameMode || !containerRef.current) return;

    const params = new URL(window.location.href).searchParams;
    const origin = params.get('origin');

    if (!origin) return;

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        window.parent.postMessage(
          { iframeHeight: containerRef.current.offsetHeight },
          origin
        );
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [isFrameMode]);

  const { t } = useTranslation();

  const weightConfig = getWeightConfig(measures);
  const lengthConfig = getLengthConfig(measures);

  const initialValues: FormValues = {
    departure_search_string: '',
    destination_search_string: '',
    departure_country: null,
    destination_country: null,
    weight: undefined,
    size_x: undefined,
    size_y: undefined,
    size_z: undefined,
    volumetricWeight: 0,
    validationContext: {},
  };

  const dimensionsValidationSchema = Yup.object({
    weight: Yup.number()
      .required('fields_required')
      .transform(numberTransform)
      .test(
        'value-is-big',
        t('max_weight_with_value', {
          value: deliveryLimitations?.maxWeight,
          unit: weightConfig.suffix,
        }),
        (value) => getMaxValidation(value, deliveryLimitations?.maxWeight)
      )
      .test('value-is-low', 'weight_value_too_low', (value) =>
        getMinValidation(value, deliveryLimitations?.minWeight)
      ),
    size_x: Yup.number()
      .required('fields_required')
      .transform(numberTransform)
      .test('value-is-big', 'width_value_too_big', (value) =>
        getMaxValidation(value, deliveryLimitations?.maxSide)
      )
      .test('value-is-low', 'width_value_too_low', (value) =>
        getMinValidation(value)
      ),
    size_y: Yup.number()
      .required('fields_required')
      .transform(numberTransform)
      .test('value-is-big', 'height_value_too_big', (value) =>
        getMaxValidation(value, deliveryLimitations?.maxSide)
      )
      .test('value-is-low', 'height_value_too_low', (value) =>
        getMinValidation(value)
      ),
    size_z: Yup.number()
      .required('fields_required')
      .transform(numberTransform)
      .test('value-is-big', 'length_value_too_big', (value) =>
        getMaxValidation(value, deliveryLimitations?.maxSide)
      )
      .test('value-is-low', 'length_value_too_low', (value) =>
        getMinValidation(value)
      ),
    volumetricWeight: Yup.number().test('vol-weight-is-big', (value) => {
      if (!value || !deliveryLimitations?.maxVolWeight) return true;
      return value <= deliveryLimitations.maxVolWeight;
    }),
  });

  const routeValidationSchemaBasic = Yup.object({
    departure_search_string: Yup.string().required('fields_required'),
    destination_search_string: Yup.string().required('fields_required'),
    validationContext: Yup.mixed().nullable().notRequired().strip(),
  });

  const departureValidationSchemaCountry = Yup.object({
    departure_country: Yup.object().required('fields_required'),
  });

  const destinationValidationSchemaCountry = Yup.object({
    destination_country: Yup.object()
      .notRequired()
      .test(
        'country is valid',
        'calc_error_no_locations_found',
        (country) => country !== null
      ),
  });

  const departureValidationSchemaZip = Yup.object({
    departure_country: Yup.object()
      .notRequired()
      .test(
        'must_select_from_dropdown_departure',
        'calc_error_select_from_search',
        function () {
          const { departure_search_string, validationContext } = this
            .parent as FormValues;
          if (!departure_search_string) return true;
          // If there were predictions, user must confirm selection
          if (
            validationContext?.predictionsNotFoundDeparture === false &&
            validationContext?.departureSelectionConfirmed !== true
          )
            return false;
          return true;
        }
      )
      .test(
        'predictions_not_found',
        'calc_error_no_locations_found',
        function () {
          const { departure_search_string } = this.parent as FormValues;
          if (!departure_search_string) return true;
          const { validationContext } = this.parent as FormValues;
          return !validationContext?.predictionsNotFoundDeparture;
        }
      )
      .test(
        'departure_not_supported',
        'calc_error_location_is_not_supported',
        function () {
          const { departure_search_string } = this.parent as FormValues;
          if (!departure_search_string) return true;
          const { validationContext } = this.parent as FormValues;
          return !validationContext?.departureNotSupported;
        }
      )
      .test(
        'failed_to_find_postal_code',
        'calc_error_failed_to_find_info',
        function (country) {
          const { departure_search_string } = this.parent as FormValues;
          if (!departure_search_string) return true;
          if (!isShipmentCountry(country)) return false;

          return !!country.zipCode;
        }
      )
      .test(
        'domestic_calculator_user_location_outside_domestic_region',
        'calc_error_domestic_user_location_outside_domestic_region',
        function (country) {
          if (!isDomesticFlow) return true;
          const { departure_search_string } = this.parent as FormValues;
          if (!departure_search_string) return true;

          if (!isShipmentCountry(country)) return false;
          return country.code === selectedCountry?.country_code;
        }
      ),
  });

  const destinationValidationSchemaZip = Yup.object({
    destination_country: Yup.object()
      .notRequired()
      .test(
        'must_select_from_dropdown_destination',
        'calc_error_select_from_search',
        function () {
          const { destination_search_string, validationContext } = this
            .parent as FormValues;
          if (!destination_search_string) return true;
          if (
            validationContext?.predictionsNotFoundDestination === false &&
            validationContext?.destinationSelectionConfirmed !== true
          )
            return false;
          return true;
        }
      )
      .test(
        'predictions_not_found',
        'calc_error_no_locations_found',
        function () {
          const { destination_search_string } = this.parent as FormValues;
          if (!destination_search_string) return true;
          const { validationContext } = this.parent as FormValues;
          return !validationContext?.predictionsNotFoundDestination;
        }
      )
      .test(
        'delivery_not_supported_to_destination',
        'calc_error_delivery_is_not_supported',
        function () {
          const { destination_search_string } = this.parent as FormValues;
          if (!destination_search_string) return true;
          const { validationContext } = this.parent as FormValues;
          return !validationContext?.destinationNotSupported;
        }
      )
      .test(
        'failed_to_find_postal_code',
        'calc_error_failed_to_find_info',
        function (country) {
          const { destination_search_string } = this.parent as FormValues;
          if (!destination_search_string) return true;
          if (!isShipmentCountry(country)) return false;

          return !!country.zipCode;
        }
      )
      .test(
        'domestic_calculator_user_location_outside_domestic_region',
        'calc_error_domestic_user_location_outside_domestic_region',
        function (country) {
          if (!isDomesticFlow) return true;
          const { destination_search_string } = this.parent as FormValues;
          if (!destination_search_string) return true;

          if (!isShipmentCountry(country)) return false;
          return country.code === selectedCountry?.country_code;
        }
      ),
  });

  const validationSchema = routeValidationSchemaBasic
    .concat(
      useZipSearchForDeparture
        ? departureValidationSchemaZip
        : departureValidationSchemaCountry
    )
    .concat(
      useZipSearchForDestination
        ? destinationValidationSchemaZip
        : destinationValidationSchemaCountry
    )
    .concat(dimensionsValidationSchema);

  const handleDataInputLabelButtonClick = (inputName: IDataInputNames) => {
    setInputsToShow({ ...inputsToShow, [inputName]: true });
  };

  const handleDataInputClose = (inputName?: keyof FormValues) => {
    if (inputName) {
      setInputsToShow({ ...inputsToShow, [inputName]: false });
    } else {
      setInputsToShow(DEFAULT_INPUTS_TO_SHOW);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string) => void,
    setFieldTouched: (
      field: string,
      isTouched?: boolean,
      shouldValidate?: boolean
    ) => void
  ) => {
    const { name, value } = e.target;

    setFieldTouched(name, true, false);
    setFieldValue(name, value);
  };

  const handleFormSubmit = async (
    formValues: FormValues,
    formikHelpers?: FormikHelpers<FormValues>
  ) => {
    if (!formValues.departure_country || !formValues.destination_country)
      return;

    const {
      departure_country: departureCountry,
      destination_country: destinationCountry,
      ...dimensions
    } = formValues;

    const deliveryOptionsRequest = {
      departure_country: departureCountry.code,
      departure_zip: departureCountry.zipCode ?? '',
      destination_country: destinationCountry.code,
      destination_zip: destinationCountry.zipCode ?? '',
      weight: dimensions.weight ?? 0,
      size_x: dimensions.size_x ?? 0,
      size_y: dimensions.size_y ?? 0,
      size_z: dimensions.size_z ?? 0,
    };

    setDepartureCountry(departureCountry);
    setDestinationCountry(destinationCountry);

    analyticsManager.track('view_item', {
      ecommerce: {
        items: [prepareEcommerceItemForCalculator()],
      },
    });

    setIsCalculating(true);

    let errorMessage = await makeShipmentCalculation(
      deliveryOptionsRequest,
      true
    );
    if (errorMessage === ApiResponseStatus.VALIDATION_ERROR) {
      // In such case try with capital zip for departure
      const foundCountry = findShipmentCountry(
        deliveryOptionsRequest.departure_country,
        undefined
      );
      const capital = store.shipmentCountriesStore.getCapital(
        foundCountry?.code
      );
      deliveryOptionsRequest.departure_zip = capital?.default_zip || '';
      errorMessage = await makeShipmentCalculation(
        deliveryOptionsRequest,
        false
      );

      if (!errorMessage && useZipSearchForDeparture) {
        formikHelpers?.setFieldValue(
          'departure_search_string',
          deliveryOptionsRequest.departure_zip
        );
      }
    }
    setShowClearButton(true);

    if (!errorMessage) {
      setShowEstimatedDeliveryCost(true);
    } else {
      if (!formikHelpers) return;

      const { setFieldError } = formikHelpers;
      setFieldError('departure_country', errorMessage.toString());
      setFieldError('destination_country', errorMessage.toString());
    }
  };

  const errorColor = isMeestClient
    ? theme.colors.input.errorColor
    : theme.colors.namedColors.accent;

  const getCalculateBtnText = () => {
    if (selectedCountry?.country_code === 'GB' && isFrameMode) {
      return t('calculator_calculate_uk');
    }

    return t('calculator_calculate');
  };

  const geoapifyService = useMemo(() => new GeoapifyService(), []);

  const getPredictionsForDepartureZipSearch = useCallback(
    async (filter?: string) => {
      if (!filter || filter.length < 3) return undefined;

      const requestOptions: AutocompleteOptions[] = [
        {
          countryCode: selectedCountry?.country_code,
          type: 'postcode',
        },
        {
          countryCode: selectedCountry?.country_code,
          type: 'city',
        },
      ];

      for (let i = 0; i < requestOptions.length; i++) {
        const opt = requestOptions[i];
        // eslint-disable-next-line no-await-in-loop
        const predictions = await geopifyGetPredictions(
          geoapifyService,
          filter ?? '',
          opt
        );
        const isFound = predictions && predictions.length > 0;

        if (isFound) {
          return predictions;
        }
      }
    },
    [selectedCountry?.country_code, geoapifyService]
  );

  const getPredictionsForDestinationZipSearch = useCallback(
    async (filter?: string, searchType?: GeoapifyAutocompleteType) => {
      if (!filter || filter.length < 3) return undefined;

      // eslint-disable-next-line no-nested-ternary
      const allowedDestinationCountry = isDomesticFlow
        ? selectedCountry?.country_code
        : undefined;

      const requestOptions: AutocompleteOptions[] = [
        {
          countryCode: allowedDestinationCountry,
          type: 'postcode',
        },
        {
          countryCode: allowedDestinationCountry,
          type: 'city',
        },
      ];

      for (let i = 0; i < requestOptions.length; i++) {
        const opt = requestOptions[i];
        // eslint-disable-next-line no-await-in-loop
        const predictions = await geopifyGetPredictions(
          geoapifyService,
          filter ?? '',
          opt
        );
        const isFound = predictions && predictions.length > 0;

        if (isFound) {
          return predictions;
        }
      }
    },
    [selectedCountry?.country_code, geoapifyService, isDomesticFlow]
  );

  const getCountriesForDestinationSearch = useCallback(
    async (
      filter: string | undefined,
      departureCountry: CountryDetails | null
    ) => {
      if (!departureCountry || receiverCountries.length === 0) return [];

      const pinnedCountryCodes = receiverPinnedList.find(
        (x) => x.country === departureCountry.code
      )?.pinned;

      const foundCountries = Promise.resolve(
        getShipmentCountriesByFilter(
          receiverCountries.filter((x) => x.code !== departureCountry.code) ??
            [],
          filter,
          pinnedCountryCodes
        )
      );

      return foundCountries;
    },
    [receiverPinnedList, receiverCountries]
  );

  const messageBlock = (
    <MessagesContainer
      $direction='column'
      $align='start'
      $justify='start'>
      <VolumetricWeightMessage />
      <ValidationHandler
        setShowEstimatedDeliveryCost={setShowEstimatedDeliveryCost}
        handleDataInputClose={handleDataInputClose}
        activeField={activeField}
      />
    </MessagesContainer>
  );

  return (
    <CalculatoContainer
      ref={containerRef}
      $hidden={hidden}>
      <Formik
        enableReinitialize={false}
        innerRef={formikRef}
        onSubmit={handleFormSubmit}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}>
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          resetForm,
        }) => {
          const commonProps = {
            touched,
            errors,
            inputsToShow,
            weightConfig,
            lengthConfig,
            refs: {
              inputWeightRef,
              inputLengthRef,
              inputHeightRef,
              inputWidthRef,
            },
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleInputChange(e, setFieldValue, setFieldTouched);
              setShowClearButton(false);
            },
            onFocus: setActiveField,
            onDataInputClose: handleDataInputClose,
            onDataInputLabelClick: handleDataInputLabelButtonClick,
          };

          return (
            <>
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                autoComplete='off'
                style={{ width: '100%' }}>
                <DataInputFields>
                  <FlexContainer
                    $width='100%'
                    $direction='column'
                    $align='start'>
                    <DataInputFieldsContainer $width='100%'>
                      {useZipSearchForDeparture && (
                        <DataInputField $minWidth='228'>
                          <InputZipFormField
                            onSelect={() => {
                              setShowClearButton(false);
                            }}
                            onDataInputLabelButtonClick={
                              handleDataInputLabelButtonClick
                            }
                            showInput={inputsToShow.departure_search_string}
                            toggleInput={(value: boolean) => {
                              setInputsToShow({
                                ...inputsToShow,
                                departure_search_string: value,
                              });
                            }}
                            getSearchOptions={
                              getPredictionsForDepartureZipSearch
                            }
                            label='from'
                            name='departure_search_string'
                            isDeparture
                            hiddenInputName='departure_country'
                            isFrameMode={isFrameMode}
                            onChange={() => {
                              setShowClearButton(false);
                            }}
                            onFocus={() => {
                              setActiveField('departure_search_string');
                            }}
                            onBlur={() => {
                              setShowClearButton(false);
                              setActiveField(null);
                            }}
                            disabled={false}
                          />
                        </DataInputField>
                      )}
                      {!useZipSearchForDeparture && (
                        <DataInputField
                          $isDisabled
                          $minWidth='228'>
                          <DataInputLabelButton>
                            {t('from')}
                          </DataInputLabelButton>
                          <FormFieldText
                            $mb='0.5rem'
                            $borderNone
                            validationMethod='always'
                            label='from'
                            name='departure_search_string'
                            isLabelHidden
                            type='text'
                            showValidationError={false}
                            disabled
                          />
                        </DataInputField>
                      )}

                      <DividerPipe />

                      {useZipSearchForDestination && (
                        <DataInputField $minWidth='228'>
                          <InputZipFormField
                            isDomestic={isDomesticFlow}
                            onSelect={() => {
                              setShowClearButton(false);
                            }}
                            onDataInputLabelButtonClick={
                              handleDataInputLabelButtonClick
                            }
                            showInput={inputsToShow.destination_search_string}
                            toggleInput={(value: boolean) => {
                              setInputsToShow({
                                ...inputsToShow,
                                destination_search_string: value,
                              });
                            }}
                            getSearchOptions={
                              getPredictionsForDestinationZipSearch
                            }
                            label='to'
                            name='destination_search_string'
                            isDeparture={false}
                            hiddenInputName='destination_country'
                            isFrameMode={isFrameMode}
                            onChange={() => {
                              setShowClearButton(false);
                            }}
                            onFocus={() => {
                              setActiveField('destination_search_string');
                            }}
                            onBlur={() => {
                              setShowClearButton(false);
                              setActiveField(null);
                            }}
                            disabled={!values.departure_country}
                          />
                        </DataInputField>
                      )}

                      {!useZipSearchForDestination && (
                        <DataInputField $minWidth='228'>
                          <InputCountryFormField
                            onSelect={(_) => {
                              setShowClearButton(false);
                            }}
                            onDataInputLabelButtonClick={
                              handleDataInputLabelButtonClick
                            }
                            showInput={inputsToShow.destination_search_string}
                            toggleInput={(value: boolean) => {
                              setInputsToShow({
                                ...inputsToShow,
                                destination_search_string: value,
                              });
                            }}
                            getSearchOptions={getCountriesForDestinationSearch}
                            label='to'
                            name='destination_search_string'
                            hiddenInputName='destination_country'
                            onChange={() => setShowClearButton(false)}
                            onFocus={() => {
                              setActiveField('destination_search_string');
                            }}
                            onBlur={() => {
                              setShowClearButton(false);
                              setActiveField(null);
                            }}
                            disabled={
                              !values.departure_country || isDestinationLocked
                            }
                          />
                        </DataInputField>
                      )}

                      <DividerPipe />

                      <DimensionFlexContainer $justify='left'>
                        <MeasureInput
                          name='weight'
                          errorColor={errorColor}
                          {...commonProps}
                        />

                        <DividerPipe />

                        <MeasureInput
                          name='size_x'
                          errorColor={errorColor}
                          {...commonProps}
                        />
                      </DimensionFlexContainer>

                      <DividerPipe />
                      <DimensionFlexContainer>
                        <MeasureInput
                          name='size_y'
                          errorColor={errorColor}
                          {...commonProps}
                        />

                        <DividerPipe />

                        <MeasureInput
                          name='size_z'
                          errorColor={errorColor}
                          {...commonProps}
                        />
                      </DimensionFlexContainer>
                    </DataInputFieldsContainer>

                    {isMobile && messageBlock}
                  </FlexContainer>
                  <CalculateButtonContainer>
                    {!showClearButton ? (
                      <Button
                        isLoading={
                          isCalculating ||
                          isDirectionsListApiLoading ||
                          isLoadingLimitations
                        }
                        appearance={
                          isMeestClient ? 'primary' : 'polonez-calculator'
                        }
                        onClick={(e) => {
                          if (!routeNotFoundError) return;

                          e.preventDefault();
                          openModal({
                            id: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
                            name: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
                            props: {
                              title: routeNotFoundError?.title,
                              body: routeNotFoundError?.message,
                              showNotifyButton: false,
                            },
                          });
                        }}
                        size='large'
                        type='submit'>
                        {getCalculateBtnText()}
                      </Button>
                    ) : (
                      <Button
                        size='large'
                        icon={{
                          glyph: 'x-cross',
                          position: isMeestClient ? 'left' : 'right',
                        }}
                        appearance={
                          isMeestClient ? 'secondary' : 'polonez-clear'
                        }
                        onClick={() => {
                          setInputsToShow(DEFAULT_INPUTS_TO_SHOW);
                          resetForm({
                            values: {
                              ...initialValues,
                              ...(!useZipSearchForDeparture
                                ? {
                                    departure_search_string:
                                      values.departure_search_string,
                                    departure_country: values.departure_country,
                                  }
                                : {}),
                            },
                          });
                          setShowClearButton(false);
                        }}>
                        {t(isMeestClient ? 'clear_all' : 'clear')}
                      </Button>
                    )}
                  </CalculateButtonContainer>
                </DataInputFields>
                {!isMobile && messageBlock}
                {/* IMPORTANT! FieldEffects components should be rendered AFTER input fields. Otherwise you won't be able to set their values. */}
                <DimensionFieldEffects />
                <DepartureFieldEffectsCommon />
                <DestinationFieldEffectSearchParam
                  showInput={(inputName, value) => {
                    setInputsToShow((prev) => ({
                      ...prev,
                      [inputName]: value,
                    }));
                  }}
                  isZipSearch={useZipSearchForDestination}
                  setDestinationCountryFieldDisabled={setIsDestinationLocked}
                />
                {useZipSearchForDeparture && (
                  <DepartureFieldEffectsZip
                    showInput={(inputName, value) => {
                      setInputsToShow((prev) => ({
                        ...prev,
                        [inputName]: value,
                      }));
                    }}
                  />
                )}
                {useZipSearchForDestination && <DestinationFieldEffectsZip />}

                {!useZipSearchForDeparture && <DepartureFieldEffectsCountry />}
                {!useZipSearchForDestination && (
                  <DestinationFieldEffectsCountry />
                )}
              </form>
              {showEstimatedDeliveryCost && (
                <EstimatedDeliveryCost isFrameMode={isFrameMode} />
              )}
            </>
          );
        }}
      </Formik>
    </CalculatoContainer>
  );
};

export default observer(WidgetCalculatorForm);
